<script lang="ts" setup>
import type { AnimationItem } from 'lottie-web';
import lottie from 'lottie-web';
import type { PropType} from 'vue';
import { onMounted, onUnmounted, ref } from 'vue';

/**********
 * Init
 **********/

const emit = defineEmits<{
    'AnimControl': [value: AnimationItem];
}>();

const props = defineProps({
    icon: {
        type: String as PropType<string | null>,
        default: null
    },
    speed: {
        type: Number,
        default: 1
    },
    width: {
        type: Number,
        default: -1
    },
    height: {
        type: Number,
        default: -1
    },
    loop: {
        type: Boolean,
        default: true
    },
    autoPlay: {
        type: Boolean,
        default: true
    },
    loopDelayMin: {
        type: Number,
        default: 0
    },
    loopDelayMax: {
        type: Number,
        default: 0
    },
});

type Style = {
    width: string;
    height: string;
    overflow: string;
    margin: string;
} | null;

const style = ref<Style>(null);
const anim = ref<AnimationItem>();
const lavContainerRef = ref<Element | null>(null);

async function getCustomIcon(): Promise<unknown> {
    const json = await import(`../../assets/animation/${props.icon}.json`);
    const newData = JSON.parse(JSON.stringify(json));
    return newData;
}

function getRandomInt(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min)) + min; //The maximum is exclusive and the minimum is inclusive
}

function executeLoop(): void {
    if (anim.value != null) {
        anim.value.play();
        setTimeout((): void => {
            anim.value?.stop();
            executeLoop();
        }, getRandomInt(props.loopDelayMin ?? 0, (props.loopDelayMax ?? 0) === 0 ? props.loopDelayMin ?? 0 : props.loopDelayMax ?? 0));
    }
}

async function init(): Promise<void> {
    if (props.icon == null) return;

    style.value = {
        width: props.width !== -1 ? `${props.width}px` : '100%',
        height: props.height !== -1 ? `${props.height}px` : '100%',
        overflow: 'hidden',
        margin: '0 auto',
    };

    const jsonData = await getCustomIcon();

    if (anim.value != null) {
        anim.value.destroy(); // Releases resources. The DOM element will be emptied.
    }

    anim.value = lottie.loadAnimation({
        container: lavContainerRef.value as Element,
        renderer: 'svg',
        loop: props.loop,
        autoplay: props.autoPlay,
        animationData: jsonData,
        rendererSettings: {
            progressiveLoad: false,
            hideOnTransparent: true
        }
    });

    emit('AnimControl', anim.value);

    anim.value.setSpeed(props.speed);

    if ((props.loopDelayMin ?? 0) > 0) {
        anim.value.loop = false;
        anim.value.autoplay = false;
        executeLoop();
    }
}

function dispose(): void {
    if (anim.value != null) {
        anim.value.stop();
        anim.value.destroy();
    }
}

/**********
 * Created
 **********/
onMounted(() => {
    void init();
});

onUnmounted(() => {
    dispose();
});
</script>

<template>
    <div v-if="style" ref="lavContainerRef" :style="style"></div>
</template>
